import { queryClient } from '@core/api/query';
import type { Organization } from '@core/models/organization';
import { routePaths } from '@screens/routes';
import { getGuideOrganizationId } from '@utils/dashboardNavigation.utils';
import { generatePath, matchPath } from 'react-router';
import type { GuideCallback, GuideNavigate } from 'src/analytics/guides/guides.models';

interface DashboardContext {
  readonly organizationId?: string;
  readonly workspaceId?: string;
}

type GetDashboardContext = () => DashboardContext;

export const OPEN_DASHBOARD_CALLBACK = 'open-dashboard';

/** Read live context at click time; never guess a property's workspace from past selections. */
export function openDashboard(
  navigate: GuideNavigate,
  getContext: GetDashboardContext = () => ({})
) {
  const { pathname, search } = globalThis.location;
  const context = getContext();
  const workspaceId =
    matchPath('/app/organizations/workspace/:workspaceId/*', pathname)?.params.workspaceId ??
    context.workspaceId;
  if (!workspaceId) return;

  const params = new URLSearchParams();
  const unitSystem = new URLSearchParams(search).get('unit_system');
  if (unitSystem) params.set('unit_system', unitSystem);
  const query = params.toString();
  const querySuffix = query ? `?${query}` : '';
  const path = generatePath(routePaths.workspaceDashboard, { workspaceId });
  navigate(`${path}${querySuffix}`, {
    state: { dashboardOrganizationId: context.organizationId, dashboardWorkspaceId: workspaceId },
  });
}

export function createDashboardGuideCallback(
  navigate: GuideNavigate,
  getNavigationState: () => unknown
): GuideCallback {
  return {
    name: OPEN_DASHBOARD_CALLBACK,
    callback: () =>
      openDashboard(navigate, () => {
        // The Hall MFE can update history without updating the host router's pathname.
        const organizationId = getGuideOrganizationId(
          globalThis.location.pathname,
          getNavigationState()
        );
        const organization = queryClient.getQueryData<Organization>([
          'organization',
          { orgId: organizationId },
        ]);
        return { organizationId, workspaceId: organization?.workspace_id };
      }),
  };
}
